import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { usePortalConfig } from '@shared/config';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/dialog';
import { Button } from '@shared/components/ui/button';
import { Label } from '@shared/components/ui/label';
import { Alert, AlertDescription } from '@shared/components/ui/alert';
import { Check, Loader2 } from 'lucide-react';
import { toast } from '@shared/components/ui/toast-notification';

interface CrossPortalEnrollmentModalProps {
  returnTo?: string;
}

export function CrossPortalEnrollmentModal({ returnTo = '/recent' }: CrossPortalEnrollmentModalProps) {
  const navigate = useNavigate();
  const { enrollmentState, enrollInPortal, cancelEnrollment } = useAuth();
  const { branding, id: portalId, newsletter } = usePortalConfig();
  const queryClient = useQueryClient();
  const [newsletterPref, setNewsletterPref] = useState<'none' | 'weekly' | 'daily'>('weekly');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!enrollmentState?.needsEnrollment) return null;

  const isLexzone = portalId === 'lexzone';
  const isLexwoo = portalId === 'lexwoo';
  const isRaadVanState = isLexzone || isLexwoo;
  const checkIconClass = isLexzone || isLexwoo ? 'text-primary' : 'text-green-600 dark:text-green-400';
  const weeklyTime = `Elke ${newsletter.weeklySendDay} om ${newsletter.weeklySendTime}`;

  const handleEnroll = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      await enrollInPortal(enrollmentState.targetPortal, newsletterPref);

      // Invalidate newsletter cache to force refetch when Settings page is opened
      // This marks the cache as stale, so React Query will automatically refetch
      // when the NewsletterSettings component mounts
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === 'newsletter-subscriptions'
      });

      // Show success toast
      toast.success(`Je bent succesvol aangemeld voor ${branding.name}!`);

      // Navigate to the original destination (e.g. newsletter case link)
      navigate(returnTo);
    } catch (_err) {
      setError('Er ging iets mis bij het aanmelden. Probeer het opnieuw.');
      setIsSubmitting(false);
    }
  };

  // Determine which portals user already has
  const existingPortalNames = enrollmentState.currentPortals
    .map(p => {
      switch (p) {
        case 'lexlabour': return 'LexLabour';
        case 'lexzone': return 'LexOmgevingswet';
        case 'lexwoo': return 'LexWoo';
        default: return p;
      }
    })
    .join(', ');

  // Get target portal description
  const getPortalDescription = () => {
    switch (portalId) {
      case 'lexlabour':
        return 'Arbeidsrecht jurisprudentie';
      case 'lexzone':
        return 'Omgevingswet jurisprudentie';
      case 'lexwoo':
        return 'Woo jurisprudentie';
      default:
        return '';
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && !isSubmitting && cancelEnrollment()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-xl sm:text-2xl">Account gevonden</DialogTitle>
          <DialogDescription className="text-base">
            Je hebt al een account op <strong>{existingPortalNames}</strong>.
            Meld je ook aan voor <strong>{branding.name}</strong> om toegang te krijgen tot {getPortalDescription()}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Portal info */}
          <div className="p-4 sm:p-6 bg-primary/5 border border-primary/20 rounded-lg">
            <div className="flex items-center gap-3 sm:gap-4">
              <img
                src={`/${portalId}_logo.svg`}
                alt={branding.name}
                className="h-10 w-20 sm:h-12 sm:w-24 object-contain"
              />
              <div>
                <h3 className="text-lg sm:text-xl font-bold font-serif text-foreground">{branding.name}</h3>
                <p className="text-sm text-muted-foreground">{getPortalDescription()}</p>
              </div>
            </div>
          </div>

          {/* Newsletter preference */}
          <div>
            <Label className="text-base font-medium mb-4 block">
              Kies je nieuwsbrief voorkeur
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              {/* No newsletter */}
              <div
                className={`relative p-4 sm:p-6 border-2 rounded-lg transition-all cursor-pointer ${
                  newsletterPref === 'none'
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-primary/50'
                }`}
                onClick={() => setNewsletterPref('none')}
              >
                <div className="sm:text-center mb-2 sm:mb-4">
                  <h4 className="text-base font-bold mb-1 text-foreground">Alleen toegang</h4>
                  <p className="text-xs text-muted-foreground">Geen nieuwsbrieven</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Toegang tot {branding.name}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Geen email notificaties</span>
                  </div>
                </div>
              </div>

              {/* Weekly newsletter */}
              <div
                className={`relative p-4 sm:p-6 border-2 rounded-lg transition-all cursor-pointer ${
                  newsletterPref === 'weekly'
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-primary/50'
                }`}
                onClick={() => setNewsletterPref('weekly')}
              >
                <div className="sm:text-center mb-2 sm:mb-4">
                  <h4 className="text-base font-bold mb-1 text-foreground">Wekelijks up to date</h4>
                  <p className="text-xs text-muted-foreground">{isRaadVanState ? 'Wekelijkse RvS nieuwsbrief' : 'Wekelijkse HR nieuwsbrief'}</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Toegang tot {branding.name}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>{isRaadVanState ? 'Wekelijkse RvS nieuwsbrief' : 'Wekelijkse HR nieuwsbrief'}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>{weeklyTime}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Belangrijkste uitspraken</span>
                  </div>
                </div>
              </div>

              {/* Daily + weekly newsletter */}
              <div
                className={`relative p-4 sm:p-6 border-2 rounded-lg transition-all cursor-pointer ${
                  newsletterPref === 'daily'
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-primary/50'
                }`}
                onClick={() => setNewsletterPref('daily')}
              >
                <div className="sm:text-center mb-2 sm:mb-4">
                  <h4 className="text-base font-bold mb-1 text-foreground">Dagelijks up to date</h4>
                  <p className="text-xs text-muted-foreground">Dagelijkse + wekelijkse nieuwsbrief</p>
                </div>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Alles van wekelijks</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Dagelijkse nieuwsbrief</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Elke dag om {newsletter.dailySendTime}</span>
                  </div>
                  <div className="flex items-start gap-2 text-sm text-foreground">
                    <Check className={`h-4 w-4 ${checkIconClass} mt-0.5 flex-shrink-0`} />
                    <span>Alle nieuwe uitspraken</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            type="button"
            variant="outline"
            onClick={cancelEnrollment}
            disabled={isSubmitting}
          >
            Annuleren
          </Button>
          <Button
            type="button"
            onClick={handleEnroll}
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSubmitting ? 'Aanmelden...' : `Aanmelden voor ${branding.name}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
